import { routes } from 'components/layout/routes';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  activeTabIdAtom,
  pagesAtom,
  skipNextNavigationRef,
} from 'store/private/layout/pageAtoms';

const genLabelFromRoute = (pages, route) => {
  let label = route.label;
  const checkIfExist = () => pages.some(i => i.label === label);
  let count = 1;
  // eslint-disable-next-line fp/no-loops
  while (checkIfExist()) {
    label = `${route.label} (${count})`;
    count++;
  }
  return label;
};

export const usePageManager = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useAtom(pagesAtom);
  const [activeTabId, setActiveTabId] = useAtom(activeTabIdAtom);

  const getIndexById = useCallback(
    id => pages.findIndex(p => p.id === id),
    [pages],
  );

  const closeAllTabs = useCallback(() => {
    const defaultTab = routes[0];
    setPages([{ ...defaultTab, refreshVersion: 0 }]);
    setActiveTabId('default');
  }, [setPages, setActiveTabId]);

  const refreshTabById = useCallback(
    tabId => {
      setPages(prev =>
        prev.map(p =>
          p.id === tabId
            ? { ...p, refreshVersion: (p.refreshVersion ?? 0) + 1 }
            : p,
        ),
      );
    },
    [setPages],
  );

  const handleNewPage = useCallback(
    newPage => {
      const id = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      let newTab;
      setPages(prev => {
        const label = genLabelFromRoute(prev, newPage);
        newTab = { ...newPage, id, label, refreshVersion: 0 };
        return [newTab, ...prev];
      });
      setActiveTabId(id);
      return newTab;
    },
    [setPages, setActiveTabId],
  );

  const openPage = useCallback(
    page => {
      const existingTab = pages.find(tab => tab.href === page.href);

      if (existingTab) {
        skipNextNavigationRef.current = true;
        setActiveTabId(existingTab.id);
        navigate(existingTab.href);
        return existingTab;
      }

      const newTab = handleNewPage(page);

      skipNextNavigationRef.current = true;
      navigate(page.href);

      return newTab;
    },
    [pages, handleNewPage, navigate, setActiveTabId],
  );

  const setActiveTabAndNavigate = useCallback(
    tabId => {
      const tab = pages.find(p => p.id === tabId);
      if (!tab) return;
      const currentPath = window.location.pathname + window.location.search;
      if (tab.href !== currentPath) {
        skipNextNavigationRef.current = true;
        navigate(tab.href);
      }
      setActiveTabId(tabId);
    },
    [pages, navigate, setActiveTabId],
  );

  const closeTabById = useCallback(
    id => {
      if (pages.length === 1) {
        const defaultRoute = routes[0];
        const newId = `tab-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 6)}`;
        const newTab = {
          ...defaultRoute,
          id: newId,
          href: '/',
          label: defaultRoute.label,
          refreshVersion: 0,
        };

        skipNextNavigationRef.current = true;
        setPages([newTab]);
        setActiveTabId(newId);

        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/') {
          navigate('/');
        }
        return;
      }

      const index = getIndexById(id);
      if (index === -1) return;

      const newPages = pages.filter(p => p.id !== id);
      let newActiveId = activeTabId;

      if (activeTabId === id) {
        if (index < newPages.length) {
          newActiveId =
            newPages[index]?.id ?? newPages[newPages.length - 1]?.id;
        } else if (newPages.length > 0) {
          newActiveId = newPages[newPages.length - 1].id;
        } else {
          newActiveId = 'default';
        }
      }

      setPages(newPages);
      setActiveTabAndNavigate(newActiveId);
    },
    [
      pages,
      activeTabId,
      getIndexById,
      setActiveTabAndNavigate,
      navigate,
      setPages,
      setActiveTabId,
    ],
  );

  return {
    pages,
    activeTabId,
    skipNextNavigation: skipNextNavigationRef,
    setPages,
    handleNewPage,
    openPage,
    closeTabById,
    setActiveTabId: setActiveTabAndNavigate,
    getIndexById,
    closeAllTabs,
    refreshTabById,
  };
};
